/**
 * Color de marca del comercio aplicado con criterio.
 *
 * El comprador viene redirigido desde la tienda: que el botón de pagar tenga el
 * color de esa tienda mantiene la continuidad y hace que la redirección no se
 * sienta un salto a un sitio ajeno. Se usa solo en el acento principal — el
 * resto del checkout sigue siendo Novasis Pay, que es quien resguarda el pago.
 */

/** Acepta #rgb y #rrggbb. Cualquier otra cosa se descarta: viene de afuera. */
export function normalizarColor(valor?: string): string | null {
  if (!valor) return null;
  const v = valor.trim();
  if (/^#[0-9a-f]{6}$/i.test(v)) return v.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(v)) {
    const [, r, g, b] = v.toLowerCase().match(/^#(.)(.)(.)$/)!;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return null;
}

function luminancia(hex: string): number {
  const canal = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

/** Contraste WCAG entre dos colores (1 = igual, 21 = negro sobre blanco). */
function contraste(a: string, b: string): number {
  const [l1, l2] = [luminancia(a), luminancia(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

/** Texto legible sobre el color dado, elegido por contraste real. */
export function textoSobre(fondo: string): "#FFFFFF" | "#0F172A" {
  return contraste(fondo, "#FFFFFF") >= contraste(fondo, "#0F172A") ? "#FFFFFF" : "#0F172A";
}

/** Oscurece para el estado hover, sin salirse del tono. */
function oscurecer(hex: string, factor = 0.86): string {
  const n = (i: number) =>
    Math.max(0, Math.round(parseInt(hex.slice(i, i + 2), 16) * factor))
      .toString(16)
      .padStart(2, "0");
  return `#${n(1)}${n(3)}${n(5)}`;
}

/**
 * Estilos del botón principal con el color del comercio. Devuelve `undefined`
 * si no hay color válido o si su contraste es tan bajo que el botón dejaría de
 * leerse: en ese caso manda el tema de Novasis Pay.
 */
export function botonDeMarca(brandColor?: string) {
  const color = normalizarColor(brandColor);
  if (!color) return undefined;
  const texto = textoSobre(color);
  if (contraste(color, texto) < 4.5) return undefined;
  return {
    background: color,
    color: texto,
    "&:hover": { background: oscurecer(color) },
  };
}
